import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from "@mui/material";
import { DeleteOutlined } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { postCall } from "../Api/axios";

const CssTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: "2px !important",
    height: "35px !important",
    "& fieldset": {
      borderColor: "d9d9d9",
    },
    "&:hover fieldset": {
      borderColor: "#1890ff",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1890ff",
    },
  },
});

const RenderInput = ({
  item,
  state,
  stateHandler,
  onChange,
  previewOnly,
  setFocusedField,
}) => {
  const uploadFileRef = useRef(null);
  const [isImageChanged, setIsImageChanged] = useState(false);
  const [fetchedImageSize, setFetchedImageSize] = useState(0);

  const handleFocus = (fieldId) => {
    if (setFocusedField) {
      setFocusedField(fieldId);
    }
  };

  const handleBlur = () => {
    if (setFocusedField) {
      setFocusedField(null);
    }
  };
//

  useEffect(() => {
    if (item.type !== "upload") return;
  
    const getSizeWithUnit = (size) => {
      if (size >= 1024 * 1024) {
        return (size / (1024 * 1024)).toFixed(2) + " MB";
      } else if (size >= 1024) {
        return (size / 1024).toFixed(2) + " KB";
      } else {
        return size + " bytes";
      }
    };
  
    const getImageSizeFromUrl = async () => {
      try {
        const response = await fetch(state[item.id]);
        const blob = await response.blob();
        const sizeInBytes = blob.size;
        const sizeInKilobytes = sizeInBytes / 1024;
        setFetchedImageSize(sizeInKilobytes.toFixed(2) + " KB");
      } catch (error) {
        setFetchedImageSize("2 MB");
      }
    };
  
    if (isImageChanged === false && state[item.id] !== "") {
      getImageSizeFromUrl();
    } else {
      const sizeInBytes = getSizeWithUnit(
        uploadFileRef.current?.files[0]?.size
      );
      setFetchedImageSize(sizeInBytes);
    }
  }, [isImageChanged, state, item.id, item.type]);
//  

  if (item.type === "input") {
    return (
      <div className="py-1 flex flex-col">
        <label className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block">
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>
        <CssTextField
          type={item.password ? "password" : "input"}
          required={item.required}
          size="small"
          multiline={item.multiline || false}
          maxRows={item.multiline ? 5 : 1}
          autoComplete="off"
          placeholder={item.placeholder}
          error={item.error || false}
          disabled={item?.isDisabled || previewOnly || false}
          helperText={item.error && item.helperText}
          value={state[item.id]}
          onChange={(e) =>
            stateHandler({
              ...state,
              [item.id]: item.isUperCase
                ? e.target.value.toUpperCase()
                : e.target.value,
            })
          }
          inputProps={{
            maxLength: item.maxLength || undefined,
            minLength: item.minLength || undefined,
          }}
          onFocus={() => handleFocus(item.id)}
          onBlur={handleBlur}
        />
      </div>
    );
  } else if (item.type === "number") {
    return (
      <div className="py-1 flex flex-col">
        <label className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block">
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>
        <CssTextField
          type="number"
          className="w-full h-full px-2.5 py-3.5 text-[#606161] bg-transparent !border-black"
          required={item.required}
          size="small"
          InputProps={{
            inputProps: { min: item.min || 0, max: item.max || 100000 },
          }}
          placeholder={item.placeholder}
          error={item.error || false}
          disabled={item?.isDisabled || previewOnly || false}
          helperText={item.error && item.helperText}
          value={state[item.id]}
          onChange={(e) => {
            const value = item.valueInDecimal
              ? parseFloat(e.target.value).toFixed(2)
              : e.target.value;

            const maxLength = item.maxLength || undefined;
            if (maxLength && value.length > maxLength) {
              return;
            }

            stateHandler({
              ...state,
              [item.id]: value,
            });
          }}
          inputProps={{
            step: "1",
          }}
          onFocus={() => handleFocus(item.id)}
          onBlur={handleBlur}
        />
      </div>
    );
  } else if (item.type === "radio") {
    let isDisabled = false;
    if (
      item.id === "isVegetarian" &&
      state["productCategory"] &&
      state["productCategory"] !== "f_and_b"
    ) {
      isDisabled = true;
    }
    return (
      <div className="py-1 flex flex-col">
        <FormControl component="fieldset">
          <label className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block">
            {item.title}
            {item.required && <span className="text-[#FF0000]"> *</span>}
          </label>
          <RadioGroup
            aria-label={item.id}
            name={item.id}
            value={state[item.id]}
            onChange={(e) => {
              stateHandler({ ...state, [item.id]: e.target.value });
            }}
            disabled={isDisabled}
          >
            <div className="flex flex-row">
              {item.options.map((radioItem, i) => (
                <FormControlLabel
                  disabled={
                    item?.isDisabled || isDisabled || previewOnly || false
                  }
                  key={i}
                  value={radioItem.value}
                  control={
                    <Radio
                      size="small"
                      checked={radioItem.value === state[item.id]}
                    />
                  }
                  label={
                    <div className="text-sm font-medium text-[#606161]">
                      {radioItem.key}
                    </div>
                  }
                />
              ))}
            </div>
          </RadioGroup>
        </FormControl>
      </div>
    );
  } else if (item.type === "checkbox") {
    const onChange = (e) => {
      const val = e.target.name;
      const itemIndex = state[item.id].indexOf(val);
      if (itemIndex === -1) {
        stateHandler((prevState) => {
          const newState = {
            ...prevState,
            [item.id]: [...prevState[item.id], val],
          };
          return newState;
        });
      } else {
        stateHandler((prevState) => {
          const newState = {
            ...prevState,
            [item.id]: prevState[item.id].filter((ele) => ele !== val),
          };
          return newState;
        });
      }
    };
    return (
      <div className="py-1 flex flex-col">
        <label className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block">
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>
        <FormGroup row>
          {item.options.map((checkboxItem) => (
            <FormControlLabel
              control={
                <Checkbox
                  disabled={item?.isDisabled || previewOnly || false}
                  key={checkboxItem.key}
                  onChange={onChange}
                  name={checkboxItem.value}
                  size="small"
                  checked={
                    state[item.id] &&
                    state[item.id].find((day) => day === checkboxItem.value)
                      ? true
                      : false
                  }
                />
              }
              label={
                <div
                  className="text-sm font-medium text-[#606161]"
                  key={checkboxItem.key}
                >
                  {checkboxItem.key}
                </div>
              }
            />
          ))}
        </FormGroup>
      </div>
    );
  } else if (item.type === "select") {
    return (
      <div className="py-1 flex flex-col">
        <label className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block">
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>
        <FormControl error={item.error || false}>
          <Autocomplete
            disableClearable={
              item.disableClearable !== undefined
                ? item.disableClearable
                : false
            }
            disabled={item?.isDisabled || previewOnly || false}
            size="small"
            options={item.options}
            getOptionLabel={(option) => option.key}
            value={
              state[item.id] !== "" && item.options && item.options.length > 0
                ? item.options.find((option) => option.value === state[item.id])
                : null
            }
            onChange={(event, newValue) => {
              stateHandler((prevState) => {
                if (item.id === "productCategory") {
                  const newState = {
                    ...prevState,
                    [item.id]: newValue.value || "",
                    productSubcategory1: "",
                  };
                  return newState;
                } else {
                  const newState = {
                    ...prevState,
                    [item.id]: newValue.value,
                  };
                  return newState;
                }
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  !previewOnly && !state[item.id] ? item.placeholder : ""
                }
                variant="outlined"
                error={item.error || false}
                helperText={item.error && item.helperText}
              />
            )}
          />
        </FormControl>
      </div>
    );
  } else if (item.type === "location-picker") {
    return (
      <div className="py-1 flex flex-col">
        <label className="text-sm py-2 ml-1 mb-1 font-medium text-left text-[#606161] inline-block">
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>
        <div style={{ width: "100%", height: "400px" }}></div>
      </div>
    );
  } else if (item.type === "date-picker") {
    return (
      <div className="py-1 flex flex-col">
        <label className="text-sm py-2 ml-1 mb-1 font-medium text-left text-[#606161] inline-block">
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>
      </div>
    );
  } else if (item.type === "time-picker") {
    return (
      <div className="py-1 flex flex-col" style={{ position: "relative" }}>
        {item.title && (
          <label className="text-sm py-2 ml-1 mb-1 font-medium text-left text-[#606161] inline-block">
            {item.title}
            {item.required && <span className="text-[#FF0000]"> *</span>}
          </label>
        )}
      </div>
    );
  } else if (item.type === "days-picker") {
    return (
      <div className="py-1 flex flex-col">
        <label className="text-sm py-2 ml-1 mb-1 font-medium text-left text-[#606161] inline-block">
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>
      </div>
    );
  } else if (item.type === "multi-select") {
    return (
      <div className="py-1 flex flex-col">
        {item.title && (
          <label className="text-sm py-2 ml-1 mb-1 font-medium text-left text-[#606161] inline-block">
            {item.title}
            {item.required && <span className="text-[#FF0000]"> *</span>}
          </label>
        )}
        <FormControl>
          <Autocomplete
            disabled={item?.isDisabled || previewOnly || false}
            multiple
            size="small"
            options={item.options}
            getOptionLabel={(option) => option.key}
            value={state[item.id]}
            onChange={(event, newValue) => {
              stateHandler((prevState) => {
                const newState = {
                  ...prevState,
                  [item.id]: newValue,
                };
                return newState;
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  state[item.id].length === 0 ? item.placeholder : ""
                }
                variant="outlined"
                error={item.error || false}
                helperText={item.error && item.helperText}
              />
            )}
          />
        </FormControl>
      </div>
    );
  } else if (item.type === "upload") {
    const allowedMaxSize = 2 * 1024 * 1024; // 2 MB in Bytes
    const getSignUrl = async (file) => {
      const url = `/api/v2/upload/${item?.file_type}`;
      const file_type = file.type.split("/")[1];
      const data = {
        fileName: file.name.replace(`.${file_type}`, ""),
        fileType: file_type,
      };
      const res = await postCall(url, data);
      return res;
    };

    const renderUploadedUrls = () => {
      if (item?.multiple) {
        if (state?.uploaded_urls) {
          return state?.uploaded_urls?.map((url) => {
            return (
              <img
                src={url}
                height={50}
                width={50}
                style={{ margin: "10px" }}
                alt="Uploaded content"
              />
            );
          });
        }
      } else {
        if ((!isImageChanged && state?.tempURL?.[item.id]) || state[item.id]) {
          return (
            <img
              src={state?.tempURL?.[item.id] || state[item.id] || ""}
              height={50}
              width={50}
              style={{ margin: "10px" }}
              alt="Uploaded content"
            />
          );
        } else {
          return <></>;
        }
      }
    };

    if (previewOnly) {
      if (typeof state[item.id] == "string") {
        return (
          <div
            style={{ height: 100, width: 100, marginBottom: 40, marginTop: 10 }}
          >
            <label
              className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block"
              style={{ width: 200 }}
            >
              {item.title}
            </label>
            <img
              className="ml-1 h-full w-full"
              src={state[item.id]}
              alt="Preview content"
            />
          </div>
        );
      } else {
        return (
          <div
            style={{ height: 100, width: 100, marginBottom: 40, marginTop: 10 }}
            className="flex"
          >
            <label className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block">
              {item.title}
            </label>
            {state[item.id]?.map((img_url) => (
              <img
                className="ml-1 h-full w-full"
                key={img_url}
                src={img_url}
                alt="Preview content"
              />
            ))}
          </div>
        );
      }
    }

    const UploadedFile = ({ name, size }) => {
      if (!name) return;

      const getImageName = (path) => {
        const splitPath = path.split("/");
        const fileTypeIndex = splitPath.indexOf(item.file_type);

        if (fileTypeIndex !== -1 && fileTypeIndex + 1 < splitPath.length) {
          const nameUrl = splitPath[fileTypeIndex + 1];
          return nameUrl;
        } else {
          return item.file_type;
        }
      };
      const getImageType = (path) => {
        const splitPath = path.split("/");
        const fileType = splitPath[splitPath.length - 1].split(".").pop();
        return fileType;
      };

      return (
        <Stack
          direction="row"
          spacing={1}
          alignItems={"center"}
          style={{ marginBottom: 20 }}
        >
          <IconButton
            style={{ width: 35, height: 35 }}
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              uploadFileRef.current.value = null;
              stateHandler((prevState) => {
                const newState = {
                  ...prevState,
                  [item.id]: Array.isArray(prevState[item.id])
                    ? prevState[item.id].filter((ele) => ele !== name)
                    : "",
                  uploaded_urls: [],
                };
                return newState;
              });
            }}
          >
            <DeleteOutlined fontSize="small" />
          </IconButton>
          <div>
            <div className="flex items-center">
              <p className="text-xs text-neutral-900 max-w-sm">
                File name: &nbsp;
              </p>
              <p className="text-xs text-neutral-600 max-w-sm">
                {getImageName(name)}
              </p>
            </div>
            <div className="flex items-center">
              <p className="text-xs text-neutral-900 max-w-sm">
                File type: &nbsp;
              </p>
              <p className="text-xs text-neutral-600 max-w-sm">
                {getImageType(name)}
              </p>
            </div>
            {!item.multiple && (
              <div className="flex items-center">
                <p className="text-xs text-neutral-900 max-w-sm">
                  File size: &nbsp;
                </p>
                <p className="text-xs text-neutral-600 max-w-sm">
                  {fetchedImageSize}
                </p>
              </div>
            )}
          </div>
        </Stack>
      );
    };

    return (
      <div className="py-1 flex flex-col">
        <label
          htmlFor="contained-button-file"
          className="text-sm py-2 ml-1 font-medium text-left text-[#606161] inline-block"
        >
          {item.title}
          {item.required && <span className="text-[#FF0000]"> *</span>}
        </label>

        <div style={{ display: "flex" }}>{renderUploadedUrls()}</div>
        <FormControl error={item.error}>
          <input
            ref={uploadFileRef}
            id="contained-button-file"
            name="contained-button-file"
            style={{
              opacity: "none",
              color: item.fontColor ? item.fontColor : "#f0f0f0",
              marginBottom: 10,
            }}
            accept="image/*"
            type="file"
            multiple={item?.multiple || false}
            key={item?.id}
            onChange={(e) => {
              const token = Cookies.get("token");
              for (const file of e.target.files) {
                if (!file.type.startsWith("image/")) {
                  toast.warn("Only image files are allowed");
                  uploadFileRef.current.value = null;
                  return;
                }
                if (file.size > allowedMaxSize) {
                  toast.warn("File size should be less than 2 MB");
                  uploadFileRef.current.value = null;
                  return;
                }
                const formData = new FormData();
                formData.append("file", file);
                getSignUrl(file).then((d) => {
                  const url = d.urls;
                  axios(url, {
                    method: "PUT",
                    data: file,
                    headers: {
                      ...(token && { "access-token": `Bearer ${token}` }),
                      "Content-Type": "multipart/form-data",
                    },
                  })
                    .then((response) => {
                      setIsImageChanged(true);
                      if (item.multiple) {
                        stateHandler((prevState) => {
                          const newState = {
                            ...prevState,
                            [item.id]: [...prevState[item.id], d.path],
                            uploaded_urls: [],
                          };
                          return newState;
                        });
                      } else {
                        let reader = new FileReader();
                        let tempUrl = "";
                        reader.onload = function (e) {
                          tempUrl = e.target.result;
                          stateHandler({
                            ...state,
                            [item.id]: d.path,
                            tempURL: {
                              ...state.tempURL,
                              [item.id]: tempUrl,
                            },
                          });
                        };
                        reader.readAsDataURL(file);
                      }
                      response.json();
                    })
                    .then((json) => {});
                });
              }
            }}
          />

          {item.multiple ? (
            state[item.id]?.map((name) => {
              return <UploadedFile name={name} key={name} />;
            })
          ) : (
            <UploadedFile name={state[item.id]} />
          )}
          {item.error && <FormHelperText>{item.helperText}</FormHelperText>}
        </FormControl>
      </div>
    );
  } else if (item.type === "label") {
    return <p className="text-2xl font-semibold mb-4 mt-14">{item.title}</p>;
  }
};

export default RenderInput;
